import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Card, ListGroup, Badge, Alert } from "react-bootstrap";
import { FaGraduationCap, FaSearch, FaArrowRight, FaCheck, FaFilter, FaChalkboardTeacher } from "react-icons/fa";

const GradeChangeManagementModal = ({ show, onHide, schoolid, getClassesBySchoolId, bulkUpdateClassGrades }) => {
  const [classes, setClasses] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedTargetGrade, setSelectedTargetGrade] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Filter states
  const [currentGradeFilter, setCurrentGradeFilter] = useState("all");
  const [academicYearFilter, setAcademicYearFilter] = useState("all");
  const [classSearchTerm, setClassSearchTerm] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Available grade levels using boolean system
  const gradeLevels = [
    { value: "all", label: "All Grades", boolean: null, numeric: null },
    { value: "grade_level1", label: "Junior Infants", boolean: "grade_level1", numeric: 1 },
    { value: "grade_level2", label: "Senior Infants", boolean: "grade_level2", numeric: 2 },
    { value: "grade_level3", label: "1st Class", boolean: "grade_level3", numeric: 3 },
    { value: "grade_level4", label: "2nd Class", boolean: "grade_level4", numeric: 4 },
    { value: "grade_level5", label: "3rd Class", boolean: "grade_level5", numeric: 5 },
    { value: "grade_level6", label: "4th Class", boolean: "grade_level6", numeric: 6 },
    { value: "grade_level7", label: "5th Class", boolean: "grade_level7", numeric: 7 },
    { value: "grade_level8", label: "6th Class", boolean: "grade_level8", numeric: 8 }
  ];

  // Available target grades (excludes "all")
  const targetGrades = gradeLevels.filter(grade => grade.value !== "all");

  // Academic years based on date_updated
  const academicYears = [
    { value: "all", label: "All Academic Years" },
    { value: "25/26", label: "25/26 Academic Year" },
    { value: "24/25", label: "24/25 Academic Year" },
    { value: "23/24", label: "23/24 Academic Year" },
    { value: "older", label: "Older School Years" }
  ];

  const getGradeLabelFromClass = (classItem) => {
    if (classItem.grade_level1) return "Junior Infants";
    if (classItem.grade_level2) return "Senior Infants";
    if (classItem.grade_level3) return "1st Class";
    if (classItem.grade_level4) return "2nd Class";
    if (classItem.grade_level5) return "3rd Class";
    if (classItem.grade_level6) return "4th Class";
    if (classItem.grade_level7) return "5th Class";
    if (classItem.grade_level8) return "6th Class";
    return "Unknown Grade";
  };

  const getAcademicYearFromDate = (dateString) => {
    if (!dateString) return "older";
    
    const date = new Date(dateString);
    
    // Academic year boundaries (August 15th)
    const aug15_2023 = new Date('2023-08-15');
    const aug15_2024 = new Date('2024-08-15');
    const aug15_2025 = new Date('2025-08-15');
    const aug15_2026 = new Date('2026-08-15');
    
    if (date >= aug15_2025 && date < aug15_2026) return "25/26";
    if (date >= aug15_2024 && date < aug15_2025) return "24/25";
    if (date >= aug15_2023 && date < aug15_2024) return "23/24";
    return "older";
  };

  const getGradeBooleanFromClass = (classItem) => {
    if (classItem.grade_level1) return "grade_level1";
    if (classItem.grade_level2) return "grade_level2";
    if (classItem.grade_level3) return "grade_level3";
    if (classItem.grade_level4) return "grade_level4";
    if (classItem.grade_level5) return "grade_level5";
    if (classItem.grade_level6) return "grade_level6";
    if (classItem.grade_level7) return "grade_level7";
    if (classItem.grade_level8) return "grade_level8";
    return "unknown";
  };

  const getStudentCount = (classItem) => {
    // Assuming student count is available in the class data
    return classItem.student_count || classItem.students?.length || 0;
  };

  useEffect(() => {
    if (show && schoolid) {
      loadData();
    }
  }, [show, schoolid]);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const classesResponse = await getClassesBySchoolId(schoolid);

      console.log("Classes response:", classesResponse); // Debug log

      // Transform class data to match component expectations
      const transformedClasses = (classesResponse || []).map(classItem => {
        const gradeLabel = getGradeLabelFromClass(classItem);
        const gradeBoolean = getGradeBooleanFromClass(classItem);
        const academicYear = getAcademicYearFromDate(classItem.date_updated);
        
        return {
          id: classItem.id,
          name: classItem.name,
          grade: gradeLabel,
          gradeBoolean: gradeBoolean,
          academicYear: academicYear,
          teacher: classItem.teacher?.name || classItem.teacherName || '',
          teacherId: classItem.teacherId,
          active: classItem.active,
          trial_class: classItem.trial_class,
          date_updated: classItem.date_updated,
          student_count: getStudentCount(classItem),
          // Keep original boolean fields for reference
          grade_level1: classItem.grade_level1,
          grade_level2: classItem.grade_level2,
          grade_level3: classItem.grade_level3,
          grade_level4: classItem.grade_level4,
          grade_level5: classItem.grade_level5,
          grade_level6: classItem.grade_level6,
          grade_level7: classItem.grade_level7,
          grade_level8: classItem.grade_level8
        };
      }).sort((a, b) => {
        // Sort by date_updated (newest first), then by grade level
        const dateA = new Date(a.date_updated || 0);
        const dateB = new Date(b.date_updated || 0);
        if (dateB.getTime() !== dateA.getTime()) {
          return dateB - dateA;
        }
        // Secondary sort by grade level
        const gradeOrder = ['grade_level1', 'grade_level2', 'grade_level3', 'grade_level4', 'grade_level5', 'grade_level6', 'grade_level7', 'grade_level8'];
        return gradeOrder.indexOf(a.gradeBoolean) - gradeOrder.indexOf(b.gradeBoolean);
      });

      console.log("Transformed classes:", transformedClasses); // Debug log

      setClasses(transformedClasses);

    } catch (err) {
      setError("Failed to load classes. Please try again.");
      console.error("Error loading classes:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClassSelect = (classItem) => {
    setSelectedClasses(prev => {
      const isSelected = prev.find(c => c.id === classItem.id);
      if (isSelected) {
        return prev.filter(c => c.id !== classItem.id);
      } else {
        return [...prev, classItem];
      }
    });
  };

  const handleSelectAllClasses = () => {
    const filteredClasses = getFilteredClasses();
    if (selectedClasses.length === filteredClasses.length) {
      setSelectedClasses([]);
    } else {
      setSelectedClasses(filteredClasses);
    }
  };

  const handleGradeSelect = (grade) => {
    setSelectedTargetGrade(grade);
  };

  const getFilteredClasses = () => {
    return classes.filter(classItem => {
      const matchesGrade = currentGradeFilter === "all" || classItem.gradeBoolean === currentGradeFilter;
      const matchesAcademicYear = academicYearFilter === "all" || classItem.academicYear === academicYearFilter;
      const matchesSearch = classItem.name?.toLowerCase().includes(classSearchTerm.toLowerCase()) ||
                           (classItem.teacher && classItem.teacher.toLowerCase().includes(classSearchTerm.toLowerCase()));
      return matchesGrade && matchesAcademicYear && matchesSearch;
    });
  };

  const handleGradeChangeClick = () => {
    if (selectedClasses.length === 0 || !selectedTargetGrade) {
      setError("Please select classes and a target grade level.");
      return;
    }
    
    // Check if any selected classes are already at the target grade
    const alreadyAtTargetGrade = selectedClasses.filter(c => c.gradeBoolean === selectedTargetGrade.boolean);
    if (alreadyAtTargetGrade.length > 0) {
      setError(`Some classes are already at ${selectedTargetGrade.label}. Please review your selection.`);
      return;
    }
    
    setShowConfirmModal(true);
  };

  const handleConfirmGradeChange = async () => {
    setShowConfirmModal(false);
    await handleGradeChange();
  };

  const handleGradeChange = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Prepare the data for your API
      const classIds = selectedClasses.map(classItem => classItem.id);
      const targetGradeLevel = selectedTargetGrade.numeric; // Use numeric value (1, 2, 3, etc.)

      // Use your bulk update API
      const response = await bulkUpdateClassGrades(classIds, targetGradeLevel);

      // Handle the response based on your API's return format
      if (response && response.success !== false) {
        const updatedCount = response.updatedCount || response.updated?.length || classIds.length;
        setSuccess(`Successfully updated ${updatedCount} class(es) to ${selectedTargetGrade.label}`);
        
        // Check if there were any failures (depending on your API response format)
        if (response.failed && response.failed.length > 0) {
          setError(`Warning: ${response.failed.length} class(es) could not be updated.`);
        }
      } else {
        setError("No classes were updated. Please try again.");
      }

      setSelectedClasses([]);
      setSelectedTargetGrade(null);
      
      // Reload data to reflect changes
      await loadData();

    } catch (err) {
      setError(`Failed to update class grades: ${err.message}`);
      console.error("Grade change error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedClasses([]);
    setSelectedTargetGrade(null);
    setError("");
    setSuccess("");
    setCurrentGradeFilter("all");
    setAcademicYearFilter("all");
    setClassSearchTerm("");
    setShowConfirmModal(false);
    onHide();
  };

  const filteredClasses = getFilteredClasses();

  return (
    <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaGraduationCap className="me-2" />
          Grade Change Management
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ minHeight: '600px' }}>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Row className="h-100">
          {/* Left Panel - Classes */}
          <Col md={7} className="border-end">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                Classes 
                <Badge bg="primary" className="ms-2">{selectedClasses.length} selected</Badge>
              </h5>
              <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={handleSelectAllClasses}
                disabled={filteredClasses.length === 0}
              >
                {selectedClasses.length === filteredClasses.length ? "Deselect All" : "Select All"}
              </Button>
            </div>

            {/* Class Filters */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Select 
                  value={academicYearFilter} 
                  onChange={(e) => setAcademicYearFilter(e.target.value)}
                  size="sm"
                >
                  {academicYears.map(year => (
                    <option key={year.value} value={year.value}>
                      {year.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Select 
                  value={currentGradeFilter} 
                  onChange={(e) => setCurrentGradeFilter(e.target.value)}
                  size="sm"
                >
                  {gradeLevels.map(grade => (
                    <option key={grade.value} value={grade.value}>
                      {grade.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4}>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    placeholder="Search classes..."
                    value={classSearchTerm}
                    onChange={(e) => setClassSearchTerm(e.target.value)}
                    size="sm"
                  />
                  <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                </div>
              </Col>
            </Row>

            {/* Classes List */}
            <div style={{ height: '450px', overflowY: 'auto' }}>
              {loading ? (
                <div className="text-center py-4">Loading classes...</div>
              ) : filteredClasses.length === 0 ? (
                <div className="text-center py-4 text-muted">No classes found</div>
              ) : (
                <div className="d-grid gap-2">
                  {filteredClasses.map(classItem => (
                    <Card
                      key={classItem.id}
                      className={`cursor-pointer ${
                        selectedClasses.find(c => c.id === classItem.id) ? 'border-primary bg-light' : ''
                      }`}
                      onClick={() => handleClassSelect(classItem)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Card.Body className="py-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-1">
                              <div className="fw-bold me-2">{classItem.name}</div>
                              <Badge 
                                bg={classItem.trial_class ? "warning" : "secondary"} 
                                className="me-2"
                              >
                                {classItem.grade}
                              </Badge>
                              {classItem.trial_class && (
                                <Badge bg="warning" text="dark">Fake</Badge>
                              )}
                            </div>
                            <div className="small text-muted">
                              <FaChalkboardTeacher className="me-1" />
                              {classItem.teacher || 'No teacher assigned'}
                              {classItem.student_count > 0 && (
                                <span className="ms-2">• {classItem.student_count} students</span>
                              )}
                              {classItem.academicYear && classItem.academicYear !== "older" && (
                                <span className="ms-2">• {classItem.academicYear}</span>
                              )}
                            </div>
                          </div>
                          <div className="text-end">
                            {selectedClasses.find(c => c.id === classItem.id) && (
                              <FaCheck className="text-primary" size={20} />
                            )}
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </Col>

          {/* Right Panel - Target Grades */}
          <Col md={5}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Target Grade Level</h5>
              {selectedTargetGrade && (
                <Badge bg="success">
                  Selected: {selectedTargetGrade.label}
                </Badge>
              )}
            </div>

            <div className="mb-3">
              <small className="text-muted">
                Select the grade level to transition the selected classes to:
              </small>
            </div>

            {/* Target Grades List */}
            <div style={{ height: '500px', overflowY: 'auto' }}>
              <div className="d-grid gap-2">
                {targetGrades.map(grade => (
                  <Card
                    key={grade.value}
                    className={`cursor-pointer ${
                      selectedTargetGrade?.value === grade.value ? 'border-success bg-light' : ''
                    }`}
                    onClick={() => handleGradeSelect(grade)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Card.Body className="py-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="fw-medium">{grade.label}</div>
                          <small className="text-muted">
                            Grade Level {grade.value.slice(-1)}
                          </small>
                        </div>
                        <div className="text-end">
                          {selectedTargetGrade?.value === grade.value && (
                            <FaCheck className="text-success" size={20} />
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </div>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div className="text-muted">
            {selectedClasses.length > 0 && selectedTargetGrade && (
              <span>
                <FaArrowRight className="me-1" />
                Changing {selectedClasses.length} class(es) to {selectedTargetGrade.label}
              </span>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={handleClose} className="me-2">
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleGradeChangeClick}
              disabled={selectedClasses.length === 0 || !selectedTargetGrade || loading}
            >
              {loading ? "Updating..." : "Update Grade Levels"}
            </Button>
          </div>
        </div>
      </Modal.Footer>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title className="d-flex align-items-center">
            <FaGraduationCap className="me-2" />
            Confirm Grade Level Change
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="text-center mb-4">
            <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                 style={{ width: '60px', height: '60px' }}>
              <FaArrowRight className="text-primary" size={24} />
            </div>
            <h5 className="mb-2">Update {selectedClasses.length} Class{selectedClasses.length !== 1 ? 'es' : ''}</h5>
            <p className="text-muted mb-0">Please review the grade level changes below before confirming</p>
          </div>

          {/* Classes List */}
          <div className="mb-4">
            <h6 className="mb-3 d-flex align-items-center">
              <Badge bg="primary" className="me-2">{selectedClasses.length}</Badge>
              Selected Classes
            </h6>
            <div className="border rounded-3 bg-light p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {selectedClasses.map((classItem, index) => (
                <div key={classItem.id} className="d-flex justify-content-between align-items-center py-2 px-2 mb-2 bg-white rounded border-start border-primary border-3" 
                     style={{ borderStartWidth: '4px !important' }}>
                  <div>
                    <div className="fw-medium text-dark">{classItem.name}</div>
                    <small className="text-muted">
                      Current: {classItem.grade}
                      {classItem.teacher && ` • ${classItem.teacher}`}
                    </small>
                  </div>
                  <div className="text-end">
                    <Badge bg="outline-secondary" className="text-muted">
                      {classItem.grade}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Grade Change Details */}
          <div className="rounded-3 p-4 mb-4" 
               style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
            <div className="row align-items-center">
              <div className="col-5">
                <div className="text-center">
                  <div className="text-muted mb-1">FROM</div>
                  <div className="fw-medium">Current Grades</div>
                  <small className="text-muted">Various grade levels</small>
                </div>
              </div>
              <div className="col-2 text-center">
                <div className="bg-primary rounded-circle d-inline-flex align-items-center justify-content-center" 
                     style={{ width: '40px', height: '40px' }}>
                  <FaArrowRight className="text-white" size={16} />
                </div>
              </div>
              <div className="col-5">
                <div className="text-center">
                  <div className="text-muted mb-1">TO</div>
                  <div className="fw-bold text-success">{selectedTargetGrade?.label}</div>
                  <div className="small text-muted">
                    Grade Level {selectedTargetGrade?.value?.slice(-1)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Warning/Info */}
          <div className="alert alert-warning d-flex align-items-start" role="alert">
            <div className="me-2">⚠️</div>
            <div>
              <strong>Important:</strong> This action will change the grade level for all selected classes. 
              Students in these classes will automatically be associated with the new grade level. 
              Please ensure this is the intended grade transition.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-light border-0 p-4">
          <div className="d-flex w-100 justify-content-between align-items-center">
            <div className="text-muted">
              <small>
                <strong>{selectedClasses.length}</strong> class{selectedClasses.length !== 1 ? 'es' : ''} will be updated to <strong>{selectedTargetGrade?.label}</strong>
              </small>
            </div>
            <div>
              <Button variant="outline-secondary" onClick={() => setShowConfirmModal(false)} className="me-3">
                Cancel
              </Button>
              <Button variant="primary" onClick={handleConfirmGradeChange} size="lg" className="px-4">
                Confirm Grade Change
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

export default GradeChangeManagementModal;